import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Seo from '../../../shared/seo/Seo';
import { getCanonicalUrl } from '../../../shared/seo/seoMeta';

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') || 'privacy';
  const isRTL = i18n.language === 'ar';

  const handleTabChange = (newType: string) => {
    setSearchParams(newType === 'privacy' ? {} : { type: newType });
  };

  const pageTitle =
    type === 'terms'
      ? t('termsPolicy.title')
      : type === 'cookies'
      ? t('cookiesPolicy.title')
      : t('privacyPolicy.title');

  const pageSubtitle =
    type === 'terms'
      ? t('termsPolicy.subtitle')
      : type === 'cookies'
      ? t('cookiesPolicy.subtitle')
      : t('privacyPolicy.subtitle');

  const metaDescription =
    type === 'terms'
      ? t('termsPolicy.metaDescription')
      : type === 'cookies'
      ? t('cookiesPolicy.metaDescription')
      : t('privacyPolicy.metaDescription');

  const renderContent = () => {
    switch (type) {
      case 'terms':
        return <TermsContent />;
      case 'cookies':
        return <CookieContent />;
      default:
        return <PrivacyContent />;
    }
  };

  return (
    <>
      {/*
        Each ?type= tab is a distinct document sharing one path, so each needs
        its own self-referencing canonical. Without one they were three pages
        collapsing onto an untagged URL.
      */}
      <Seo
        title={`${pageTitle} - A2S SARL`}
        description={metaDescription}
        canonical={getCanonicalUrl(
          type === 'privacy' ? '/privacy-policy' : `/privacy-policy?type=${type}`
        )}
      />

      <Container dir={isRTL ? 'rtl' : 'ltr'}>
        <Header>
          <Title>{pageTitle}</Title>
          <Subtitle>{pageSubtitle}</Subtitle>
        </Header>

        <TabContainer>
          <Tab active={type === 'privacy'} onClick={() => handleTabChange('privacy')}>
            {t('common.privacy')}
          </Tab>
          <Tab active={type === 'terms'} onClick={() => handleTabChange('terms')}>
            {t('common.terms')}
          </Tab>
          <Tab active={type === 'cookies'} onClick={() => handleTabChange('cookies')}>
            {t('common.cookies')}
          </Tab>
        </TabContainer>

        <ContentContainer>{renderContent()}</ContentContainer>
      </Container>
    </>
  );
};

const PrivacyContent: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <Section>
        <SectionTitle>{t('privacyPolicy.sections.collection.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.collection.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.usage.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.usage.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.protection.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.protection.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.cookies.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.cookies.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.links.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.links.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.changes.title')}</SectionTitle>
        <Paragraph>{t('privacyPolicy.sections.changes.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('privacyPolicy.sections.contact.title')}</SectionTitle>
        <Paragraph>
          <Trans
            i18nKey="privacyPolicy.sections.contact.text"
            components={[
              <a href={`mailto:${t('common.contact.email')}`} />
            ]}
          />
        </Paragraph>
        
      </Section>
    </>
  );
};

const TermsContent: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <Section>
        <SectionTitle>{t('termsPolicy.sections.acceptance.title')}</SectionTitle>
        <Paragraph>{t('termsPolicy.sections.acceptance.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('termsPolicy.sections.usage.title')}</SectionTitle>
        <Paragraph>{t('termsPolicy.sections.usage.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('termsPolicy.sections.modifications.title')}</SectionTitle>
        <Paragraph>{t('termsPolicy.sections.modifications.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('termsPolicy.sections.intellectualProperty.title')}</SectionTitle>
        <Paragraph>{t('termsPolicy.sections.intellectualProperty.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('termsPolicy.sections.liability.title')}</SectionTitle>
        <Paragraph>{t('termsPolicy.sections.liability.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('termsPolicy.sections.contact.title')}</SectionTitle>
        <Paragraph>
          <Trans
            i18nKey="termsPolicy.sections.contact.text"
            components={[
              <a href={`mailto:${t('common.contact.email')}`} />
            ]}
          />
        </Paragraph>
      </Section>
    </>
  );
};

const CookieContent: React.FC = () => {
  const { t } = useTranslation();
  const cookieTypeItems = t('cookiesPolicy.sections.types.items', { returnObjects: true }) as string[];

  return (
    <>
      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.whatIs.title')}</SectionTitle>
        <Paragraph>{t('cookiesPolicy.sections.whatIs.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.usage.title')}</SectionTitle>
        <Paragraph>{t('cookiesPolicy.sections.usage.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.types.title')}</SectionTitle>
        <Paragraph>{t('cookiesPolicy.sections.types.text')}</Paragraph>
        <List>
          {cookieTypeItems.map((item, index) => (
            <ListItem key={index}>{item}</ListItem>
          ))}
        </List>
      </Section>

      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.control.title')}</SectionTitle>
        <Paragraph>{t('cookiesPolicy.sections.control.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.updates.title')}</SectionTitle>
        <Paragraph>{t('cookiesPolicy.sections.updates.text')}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{t('cookiesPolicy.sections.contact.title')}</SectionTitle>
        <Paragraph>
          <Trans
            i18nKey="cookiesPolicy.sections.contact.text"
            components={[
              <a href={`mailto:${t('common.contact.email')}`} />
            ]}
          />
        </Paragraph>
      </Section>
    </>
  );
};

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--spacing-xxl) var(--spacing-lg);
  font-family: 'Roboto', sans-serif;

  @media (max-width: 768px) {
    padding: var(--spacing-xl) var(--spacing-md);
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: var(--spacing-xxl);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: var(--spacing-md);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--gray);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-xxl);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? 'var(--primary)' : 'var(--gray)'};
  border-bottom: 2px solid ${props => props.active ? 'var(--primary)' : 'transparent'};
  cursor: pointer;
  transition: var(--transition);
  position: relative;

  &:hover {
    color: var(--primary);
    background-color: var(--light);
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    border-bottom: none;
    border-inline-start: 2px solid ${props => props.active ? 'var(--primary)' : 'transparent'};
  }
`;

const ContentContainer = styled.div`
  animation: fadeIn 0.5s ease-in-out;
`;

const Section = styled.section`
  padding: 0;
  margin-bottom: var(--spacing-xl);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: var(--spacing-lg);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    inset-inline-start: 0;
    width: 60px;
    height: 3px;
    background: var(--secondary);
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-color);
  margin-bottom: 0;
  overflow-wrap: anywhere;
`;

const List = styled.ul`
  margin: 0;
  padding-inline-start: var(--spacing-xl);
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-color);
  margin-bottom: var(--spacing-sm);
  position: relative;

  &::marker {
    color: var(--secondary);
  }
`;

const LegalInfo = styled.div`
  background: var(--light);
  padding: var(--spacing-lg);
  border-radius: var(--border-radius);
  border-inline-start: 4px solid var(--primary);
`;

const InfoRow = styled.div`
  margin-bottom: var(--spacing-sm);
  font-size: 0.95rem;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: var(--primary);
    font-weight: 600;
  }
`;

const ContactInfo = styled.div`
  background: var(--light);
  padding: var(--spacing-lg);
  border-radius: var(--border-radius);
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--text-color);

  strong {
    color: var(--primary);
    font-size: 1.1rem;
  }
`;

export default PrivacyPolicy;
